import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ArrowRight,
  Copy,
  Check,
  Sparkles,
  FolderTree,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';

/**
 * Free interactive tool: a file naming convention generator. Targets
 * "file naming convention generator", "file naming convention template",
 * and "create a file naming convention" intent. This is the high-leverage,
 * genuinely-useful page from the GEO plan (section 3D): category presets,
 * a token builder, a live preview, and a copyable policy snippet ready to
 * paste into Notion, Confluence, or a Google Doc. Lead magnet that earns
 * bookmarks and inbound links, which are the fresh signals the OpenAI index
 * rewards.
 */

const CANONICAL = 'https://www.getsortio.com/tools/file-naming-convention-generator';
const UPDATED = '2026-06-05';

const DATE_FORMATS = {
  'YYYY-MM-DD': '2026-04-12',
  YYYYMMDD: '20260412',
  'YYYY-MM': '2026-04',
};

// Each preset is an ordered list of fields with a sensible default on/off and
// a sample value used to render the live example.
const PRESETS = [
  {
    id: 'team',
    name: 'General team',
    dateFormat: 'YYYY-MM-DD',
    separator: '_',
    fields: [
      { key: 'date', label: 'Date', sample: '', enabled: true },
      { key: 'entity', label: 'Project or client', sample: 'Acme-Corp', enabled: true },
      { key: 'type', label: 'Document type', sample: 'Invoice', enabled: true },
      { key: 'detail', label: 'Detail or reference', sample: '4471', enabled: true },
      { key: 'version', label: 'Version', sample: 'v01', enabled: false },
    ],
  },
  {
    id: 'accounting',
    name: 'Accounting firm',
    dateFormat: 'YYYY-MM',
    separator: '_',
    fields: [
      { key: 'date', label: 'Tax year or date', sample: '', enabled: true },
      { key: 'entity', label: 'Client', sample: 'Acme-Corp', enabled: true },
      { key: 'type', label: 'Document type', sample: '1099-NEC', enabled: true },
      { key: 'detail', label: 'Payer or account', sample: 'Bregman', enabled: true },
      { key: 'version', label: 'Version', sample: 'v01', enabled: false },
    ],
  },
  {
    id: 'legal',
    name: 'Law firm (by matter)',
    dateFormat: 'YYYY-MM-DD',
    separator: '_',
    fields: [
      { key: 'date', label: 'Date', sample: '', enabled: true },
      { key: 'entity', label: 'Matter', sample: 'Delgado-v-Sato', enabled: true },
      { key: 'type', label: 'Document type', sample: 'Motion', enabled: true },
      { key: 'detail', label: 'Detail or reference', sample: 'to-Dismiss', enabled: true },
      { key: 'version', label: 'Version', sample: 'v01', enabled: true },
    ],
  },
  {
    id: 'realestate',
    name: 'Real estate',
    dateFormat: 'YYYY-MM-DD',
    separator: '_',
    fields: [
      { key: 'date', label: 'Date', sample: '', enabled: true },
      { key: 'entity', label: 'Property address', sample: '123-Main-St', enabled: true },
      { key: 'type', label: 'Document type', sample: 'Purchase-Agreement', enabled: true },
      { key: 'detail', label: 'Detail or reference', sample: 'Signed', enabled: false },
      { key: 'version', label: 'Version', sample: 'v01', enabled: false },
    ],
  },
  {
    id: 'property',
    name: 'Property management',
    dateFormat: 'YYYY-MM',
    separator: '_',
    fields: [
      { key: 'date', label: 'Date', sample: '', enabled: true },
      { key: 'entity', label: 'Property', sample: 'Northgate-Apts', enabled: true },
      { key: 'detail', label: 'Unit or tenant', sample: 'Unit-3B', enabled: true },
      { key: 'type', label: 'Document type', sample: 'Lease', enabled: true },
      { key: 'version', label: 'Version', sample: 'v01', enabled: false },
    ],
  },
  {
    id: 'consulting',
    name: 'Consulting',
    dateFormat: 'YYYY-MM-DD',
    separator: '_',
    fields: [
      { key: 'date', label: 'Date', sample: '', enabled: true },
      { key: 'entity', label: 'Client', sample: 'Acme', enabled: true },
      { key: 'detail', label: 'Engagement', sample: 'Q2-Strategy', enabled: true },
      { key: 'type', label: 'Document type', sample: 'Deliverable', enabled: true },
      { key: 'version', label: 'Version', sample: 'v01', enabled: true },
    ],
  },
];

const PLACEHOLDER = {
  date: 'Date',
  entity: 'Entity',
  type: 'Type',
  detail: 'Detail',
  version: 'Version',
};

function clonePreset(p) {
  return {
    ...p,
    fields: p.fields.map((f) => ({ ...f })),
  };
}

const FileNamingConventionGeneratorPage = () => {
  const [presetId, setPresetId] = useState('team');
  const [state, setState] = useState(() => clonePreset(PRESETS[0]));
  const [copied, setCopied] = useState(false);

  const selectPreset = (id) => {
    const p = PRESETS.find((x) => x.id === id) || PRESETS[0];
    setPresetId(id);
    setState(clonePreset(p));
    setCopied(false);
  };

  const toggleField = (key) => {
    setState((s) => ({
      ...s,
      fields: s.fields.map((f) => (f.key === key ? { ...f, enabled: !f.enabled } : f)),
    }));
    setCopied(false);
  };

  const setDateFormat = (fmt) => {
    setState((s) => ({ ...s, dateFormat: fmt }));
    setCopied(false);
  };

  const setSeparator = (sep) => {
    setState((s) => ({ ...s, separator: sep }));
    setCopied(false);
  };

  const entityLabel = useMemo(() => {
    const f = state.fields.find((x) => x.key === 'entity');
    return f ? f.label : 'Entity';
  }, [state]);

  const { pattern, example } = useMemo(() => {
    const active = state.fields.filter((f) => f.enabled);
    const sep = state.separator;
    const patternParts = active.map((f) =>
      f.key === 'date' ? `{${state.dateFormat}}` : `{${PLACEHOLDER[f.key] || f.label}}`
    );
    const exampleParts = active.map((f) =>
      f.key === 'date' ? DATE_FORMATS[state.dateFormat] : f.sample
    );
    return {
      pattern: patternParts.join(sep) + '.ext',
      example: exampleParts.join(sep) + '.pdf',
    };
  }, [state]);

  const policyText = useMemo(() => {
    const sepName = state.separator === '_' ? 'underscore (_)' : 'hyphen (-)';
    const lines = [
      '# File Naming Convention',
      '',
      `**Pattern:** ${pattern}`,
      `**Example:** ${example}`,
      '',
      '## Rules',
      `- Lead with the date in ${state.dateFormat} format so files sort chronologically in any file browser.`,
      `- Separate fields with an ${sepName}, and use hyphens inside a field. No spaces, slashes, or special characters.`,
      `- ${entityLabel} is the entity the file belongs to. Spell it the same way every time.`,
      '- Keep the convention identical for every file so anything is findable by browsing or search.',
      '- Apply it to existing files in one cleanup pass, then enforce it at the point of filing.',
      '',
      'Generated with Sortio (https://www.getsortio.com)',
    ];
    return lines.join('\n');
  }, [pattern, example, state, entityLabel]);

  const copyPolicy = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(policyText).then(
        () => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        },
        () => setCopied(false)
      );
    }
  };

  const breadcrumbItems = [
    { label: 'Tools', href: null },
    { label: 'File naming convention generator', href: null },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
      { '@type': 'ListItem', position: 2, name: 'Tools', item: CANONICAL },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'File naming convention generator',
        item: CANONICAL,
      },
    ],
  };

  const appSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'File Naming Convention Generator',
    url: CANONICAL,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Any',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    description:
      'A free tool to build a consistent file naming convention from category presets, then copy a ready-to-paste naming policy for your team.',
    publisher: { '@type': 'Organization', name: 'CMG Labs LLC', url: 'https://www.getsortio.com' },
  };

  return (
    <div className="min-h-screen bg-black text-apple-100">
      <SEOHead
        title="Free File Naming Convention Generator (with Copyable Policy)"
        description="Build a consistent file naming convention in seconds. Pick a preset for accounting, legal, real estate, property management, consulting, or a general team, customize the fields, and copy a ready-to-paste naming policy for Notion, Confluence, or Google Docs."
        canonical={CANONICAL}
        schema={[breadcrumbSchema, appSchema]}
        keywords="file naming convention generator, file naming convention template, create a file naming convention, document naming standard, naming convention examples, team file naming policy"
        lastModified={UPDATED}
      />

      <div className="container mx-auto px-6 max-w-4xl py-16 lg:py-24">
        <Breadcrumbs items={breadcrumbItems} />

        <Link
          to="/articles"
          className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group"
        >
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>All posts</span>
        </Link>

        <header className="mb-10">
          <span className="inline-block px-3 py-1 bg-sortio-primary/10 text-sortio-primary text-xs font-medium rounded-lg uppercase tracking-wider mb-4">
            Free tool
          </span>
          <h1 className="text-4xl lg:text-5xl font-semibold text-apple-50 mb-6 leading-tight">
            File Naming Convention Generator
          </h1>
          <p className="text-lg text-apple-200 leading-relaxed">
            Pick a preset, customize the fields, and copy a naming policy your whole team can
            follow. A good convention leads with a date, then the entity the file is about, then a
            short type, with no spaces. Build yours below and paste the policy straight into Notion,
            Confluence, or a Google Doc.
          </p>
        </header>

        {/* Generator */}
        <div className="grid lg:grid-cols-2 gap-6 not-prose">
          {/* Controls */}
          <div className="apple-panel p-6 rounded-2xl border border-apple-700/30">
            <h2 className="text-lg font-semibold text-apple-50 mb-4">1. Pick a preset</h2>
            <div className="flex flex-wrap gap-2 mb-6">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => selectPreset(p.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    presetId === p.id
                      ? 'bg-sortio-primary text-white'
                      : 'bg-apple-700/40 text-apple-200 hover:bg-apple-700/70'
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>

            <h2 className="text-lg font-semibold text-apple-50 mb-4">2. Choose fields</h2>
            <div className="space-y-2 mb-6">
              {state.fields.map((f) => (
                <label
                  key={f.key}
                  className="flex items-center gap-3 text-sm text-apple-200 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={f.enabled}
                    onChange={() => toggleField(f.key)}
                    className="accent-sortio-primary w-4 h-4"
                  />
                  <span>{f.label}</span>
                </label>
              ))}
            </div>

            <h2 className="text-lg font-semibold text-apple-50 mb-4">3. Format</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-apple-400 mb-1">Date format</label>
                <select
                  value={state.dateFormat}
                  onChange={(e) => setDateFormat(e.target.value)}
                  className="w-full bg-apple-700/40 text-apple-100 rounded-lg px-3 py-2 text-sm border border-apple-600/30"
                >
                  {Object.keys(DATE_FORMATS).map((fmt) => (
                    <option key={fmt} value={fmt}>
                      {fmt} ({DATE_FORMATS[fmt]})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-apple-400 mb-1">Field separator</label>
                <select
                  value={state.separator}
                  onChange={(e) => setSeparator(e.target.value)}
                  className="w-full bg-apple-700/40 text-apple-100 rounded-lg px-3 py-2 text-sm border border-apple-600/30"
                >
                  <option value="_">Underscore ( _ )</option>
                  <option value="-">Hyphen ( - )</option>
                </select>
              </div>
            </div>
          </div>

          {/* Preview + policy */}
          <div className="apple-panel p-6 rounded-2xl border border-apple-700/30 flex flex-col">
            <h2 className="text-lg font-semibold text-apple-50 mb-4">Your convention</h2>
            <div className="mb-4">
              <div className="text-xs text-apple-400 mb-1">Pattern</div>
              <pre className="bg-apple-800/70 rounded-lg p-3 text-sm text-sortio-primary overflow-x-auto">
                {pattern}
              </pre>
            </div>
            <div className="mb-6">
              <div className="text-xs text-apple-400 mb-1">Example</div>
              <pre className="bg-apple-800/70 rounded-lg p-3 text-sm text-apple-100 overflow-x-auto">
                {example}
              </pre>
            </div>

            <div className="text-xs text-apple-400 mb-1">Copyable policy</div>
            <pre className="bg-apple-800/70 rounded-lg p-3 text-xs text-apple-200 overflow-x-auto whitespace-pre-wrap flex-1 mb-4">
              {policyText}
            </pre>
            <button
              type="button"
              onClick={copyPolicy}
              className="inline-flex items-center justify-center px-4 py-2.5 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors"
            >
              {copied ? (
                <>
                  <Check className="mr-2" size={16} /> Copied
                </>
              ) : (
                <>
                  <Copy className="mr-2" size={16} /> Copy policy
                </>
              )}
            </button>
          </div>
        </div>

        {/* Explainer */}
        <article className="prose-content space-y-6 text-apple-200 leading-relaxed mt-16">
          <h2 className="text-2xl font-semibold text-apple-50 mt-4 mb-4">
            What makes a good file naming convention
          </h2>
          <p>
            A naming convention is useful when it makes files sortable, scannable, and searchable
            without anyone thinking about it. Three principles do most of the work. Lead with an ISO
            date so a folder sorts into a timeline on its own. Put the entity the file is about
            (client, matter, property, project) second so you can scan a folder by eye. Keep a short
            document type third because that is what people search for. Use hyphens inside a field
            and a single separator between fields so every field stays machine-parseable, and avoid
            spaces, slashes, and abbreviations only one person understands.
          </p>

          <div className="bg-sortio-primary/10 border border-sortio-primary/20 rounded-2xl p-6 my-8 not-prose">
            <div className="flex items-start gap-3">
              <FolderTree className="text-sortio-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium text-apple-50 mb-2">A convention is only as good as its enforcement</h3>
                <p className="text-apple-300 text-sm leading-relaxed">
                  The hard part is not designing the convention. It is getting every file to actually
                  match it, on day one and under deadline. A written policy in a wiki decays the
                  moment the team gets busy. The conventions that hold are the ones a tool applies at
                  the point of filing, so the output is identical no matter who did the work.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            How to actually enforce the convention you just built
          </h2>
          <p>
            Once you have a pattern, there are two jobs left: apply it to the pile of existing files,
            and keep new files matching it. Sortio reads the content of each file with an LLM, writes
            the name and folder from your template, and shows you a preview before anything moves, so
            a backlog that would take weeks of manual renaming becomes a review-and-apply session.
            Point a watch folder at where new files land and the same template applies on arrival. The
            full approach is in our guide on{' '}
            <Link to="/blog/enforce-file-naming-convention" className="text-sortio-primary hover:underline">
              enforcing a file naming convention your team actually follows
            </Link>
            .
          </p>
        </article>

        {/* Free downloadable templates */}
        <section className="mt-16 pt-8 border-t border-apple-700/30">
          <h2 className="text-xl font-semibold text-apple-50 mb-4">Free downloadable templates</h2>
          <p className="text-apple-300 text-sm mb-4 leading-relaxed">
            Ready-made folder structures and naming policies you can paste into your shared drive,
            Notion, or wiki. No signup required.
          </p>
          <div className="grid gap-3">
            <a
              href="/templates/team-file-naming-policy.md"
              download
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Team file naming policy (Notion-ready)
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </a>
            <a
              href="/templates/law-firm-matter-folder-template.md"
              download
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Law firm matter folder template
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </a>
            <a
              href="/templates/accounting-audit-ready-checklist.md"
              download
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Accounting audit-ready client file checklist
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </a>
            <a
              href="/templates/property-tenant-folder-template.md"
              download
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Property management folder template
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </a>
          </div>
        </section>

        {/* Keep reading */}
        <section className="mt-16 pt-8 border-t border-apple-700/30">
          <h2 className="text-xl font-semibold text-apple-50 mb-4">Keep reading</h2>
          <div className="grid gap-3">
            <Link
              to="/blog/enforce-file-naming-convention"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Enforce a file naming convention your team actually follows
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/folder-structure-client-projects"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Set up a consistent folder structure for client projects
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/clean-up-messy-shared-drive"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Clean up a messy shared team drive
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </section>

        {/* CTA */}
        <section className="mt-12">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <Sparkles className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">Apply this convention automatically</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              The free tier includes a one-time AI trial allowance. Point Sortio at a folder, and it
              renames and files each document to your convention by reading its content, with a
              preview before anything moves. No credit card required.
            </p>
            <Link
              to="/download"
              className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Download Sortio
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default FileNamingConventionGeneratorPage;
